// ./src/components/features/Categories/CategoriesFilterBar/CategoriesFilterBar.tsx
//
// Filter controls for the Categories list: by status (active / disabled) and by
// group (general / poi / …). Fully controlled — the page owns the value and maps
// it to a CategoryFilter.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { CATEGORY_GROUP_VALUES } from "@/utils/api";
import { AppIcon, SelectField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

export type CategoryStatusFilter = "active" | "disabled";

export interface CategoriesFilterValue {
  status: CategoryStatusFilter | null;
  group: string | null;
}

export interface CategoriesFilterBarProps {
  value: CategoriesFilterValue;
  onChange: (value: CategoriesFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_CATEGORIES_FILTER: CategoriesFilterValue = {
  status: null,
  group: null,
};

const isFilterActive = (value: CategoriesFilterValue): boolean => Boolean(value.status || value.group);

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };
const GROUP_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };

const ANY = "any";

const GROUP_LABEL_DEFAULTS: Record<string, string> = {
  general: "General",
  poi: "Points of interest",
  budget: "Budget",
  task: "Tasks",
  appointment: "Appointments",
  vendor: "Vendors",
};

const CategoriesFilterBarComponent: React.FC<CategoriesFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["categories", "common"]);

  const statusOptions = [
    { value: ANY, label: t("categories:filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("categories:filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("categories:filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const groupOptions = CATEGORY_GROUP_VALUES.map((group) => ({
    value: group,
    label: t(`categories:groups.${group}`, { defaultValue: GROUP_LABEL_DEFAULTS[group] ?? group }),
  }));

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("categories:filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as CategoryStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={GROUP_STYLE}>
        <SelectField
          label={t("categories:filters.group.label", { defaultValue: "Group" })}
          placeholder={t("categories:filters.group.placeholder", { defaultValue: "All groups" })}
          data={groupOptions}
          value={value.group}
          onChange={(_name, group) => onChange({ ...value, group })}
          disabled={disabled}
          clearable
          comboboxProps={DRAWER_COMBOBOX_PROPS}
          size="md"
          mt={4}
        />
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_CATEGORIES_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const CategoriesFilterBar = React.memo(CategoriesFilterBarComponent);
